import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Refresh as RefreshIcon, Lightbulb as HintIcon } from '@mui/icons-material';
import { gameService } from '../../../services/gameService';
import ScoreSubmission from '../ScoreSubmission';
import LetterTile from './LetterTile';
import { useAuth } from '../../../contexts/AuthContext';

// Word lists by difficulty
const wordLists = {
  easy: [
    'REACT', 'BRAIN', 'LEARN', 'FOCUS', 'THINK', 'SOLVE', 'MIND', 'PLAY',
    'GAME', 'CODE', 'SKILL', 'LOGIC', 'TRAIN', 'SMART', 'BUILD', 'PLAN',
    'IDEA', 'TASK', 'WORK', 'GOAL', 'READ', 'VIEW', 'GROW', 'FLEX'
  ],
  medium: [
    'MEMORY', 'PUZZLE', 'CODING', 'MENTAL', 'GROWTH', 'WISDOM', 'NEURAL',
    'PATTERN', 'PROCESS', 'ANALYZE', 'DEVELOP', 'IMPROVE', 'REFLECT', 'ACHIEVE',
    'CONCEPT', 'INSIGHT', 'COMPLEX', 'DYNAMIC', 'ENHANCE', 'EXPLORE', 'PERFORM'
  ],
  hard: [
    'COGNITION', 'CHALLENGE', 'INTELLECT', 'KNOWLEDGE', 'MINDFULNESS', 'PSYCHOLOGY',
    'ALGORITHM', 'REASONING', 'CREATIVITY', 'PERCEPTION', 'INNOVATION', 'ANALYTICAL',
    'STRATEGIC', 'DISCIPLINE', 'RESILIENCE', 'DEDICATION', 'COMPREHEND', 'VISUALIZE'
  ],
};

// Word clues by word
const wordClues = {
  // Easy words
  'REACT': { 
    definition: 'A JavaScript library for building user interfaces', 
    category: 'Programming',
    synonym: 'Respond'
  },
  'BRAIN': { 
    definition: 'The organ inside your head that controls thought and feeling', 
    category: 'Anatomy',
    synonym: 'Mind'
  },
  'LEARN': { 
    definition: 'To gain knowledge or skill by studying or experience', 
    category: 'Education',
    synonym: 'Study'
  },
  'FOCUS': { 
    definition: 'To concentrate attention or effort on something specific', 
    category: 'Cognition',
    synonym: 'Concentrate'
  },
  'THINK': { 
    definition: 'To use your mind to consider ideas and make judgments', 
    category: 'Cognition',
    synonym: 'Ponder'
  },
  'SOLVE': { 
    definition: 'To find an answer or solution to a problem', 
    category: 'Problem-solving',
    synonym: 'Resolve'
  },
  'MIND': { 
    definition: 'The element that enables consciousness and thought', 
    category: 'Cognition',
    synonym: 'Intellect'
  },
  'PLAY': { 
    definition: 'To engage in activity for enjoyment or recreation', 
    category: 'Recreation',
    synonym: 'Game'
  },
  'GAME': { 
    definition: 'An activity or contest with rules, undertaken for entertainment or recreation', 
    category: 'Recreation',
    synonym: 'Play'
  },
  'CODE': { 
    definition: 'A set of rules or instructions for a computer program', 
    category: 'Programming',
    synonym: 'Program'
  },
  'SKILL': { 
    definition: 'The ability to do something that needs practice', 
    category: 'Ability',
    synonym: 'Talent'
  },
  'LOGIC': { 
    definition: 'A system of reasoning or a particular method of reasoning', 
    category: 'Reasoning',
    synonym: 'Reason'
  },
  'TRAIN': { 
    definition: 'To teach or learn the skills needed to do a particular job or activity', 
    category: 'Education',
    synonym: 'Teach'
  },
  'SMART': { 
    definition: 'Having or showing a great deal of intelligence, common sense, or shrewdness', 
    category: 'Intelligence',
    synonym: 'Intelligent'
  },
  'BUILD': { 
    definition: 'To make or construct something by putting parts or materials together', 
    category: 'Construction',
    synonym: 'Construct'
  },
  'PLAN': { 
    definition: 'A detailed proposal for doing or achieving something', 
    category: 'Planning',
    synonym: 'Proposal'
  },
  'IDEA': { 
    definition: 'A thought or suggestion about something', 
    category: 'Thought',
    synonym: 'Concept'
  },
  'TASK': { 
    definition: 'A piece of work to be done or undertaken', 
    category: 'Work',
    synonym: 'Job'
  },
  'WORK': { 
    definition: 'An activity involving mental or physical effort done in order to achieve a purpose', 
    category: 'Activity',
    synonym: 'Job'
  },
  'GOAL': { 
    definition: 'The object of a person\'s ambition or effort', 
    category: 'Ambition',
    synonym: 'Objective'
  },
  'READ': { 
    definition: 'To look at and understand written words', 
    category: 'Reading',
    synonym: 'Study'
  },
  'VIEW': { 
    definition: 'A particular way of thinking about something or a particular attitude towards something', 
    category: 'Opinion',
    synonym: 'Opinion'
  },
  'GROW': { 
    definition: 'To increase in size, amount, or degree', 
    category: 'Development',
    synonym: 'Develop'
  },
  'FLEX': { 
    definition: 'To bend or be able to bend easily without breaking', 
    category: 'Flexibility',
    synonym: 'Bend'
  },
  
  // Medium words
  'MEMORY': { 
    definition: 'The faculty by which the mind stores and recalls information', 
    category: 'Cognition',
    synonym: 'Recollection'
  },
  'PUZZLE': { 
    definition: 'A game or problem that tests ingenuity or knowledge', 
    category: 'Games',
    synonym: 'Riddle'
  },
  'CODING': { 
    definition: 'The process of writing computer programs', 
    category: 'Programming',
    synonym: 'Programming'
  },
  'MENTAL': { 
    definition: 'Relating to the mind or processes of the mind', 
    category: 'Psychology',
    synonym: 'Cognitive'
  },
  'GROWTH': { 
    definition: 'The process of increasing in size, amount, or degree', 
    category: 'Development',
    synonym: 'Development'
  },
  'WISDOM': { 
    definition: 'The quality of having experience, knowledge, and good judgment', 
    category: 'Knowledge',
    synonym: 'Insight'
  },
  'NEURAL': { 
    definition: 'Relating to a nerve or the nervous system', 
    category: 'Neuroscience',
    synonym: 'Nervous'
  },
  'PATTERN': { 
    definition: 'A repeated or regular way of doing something', 
    category: 'Behavior',
    synonym: 'Habit'
  },
  'PROCESS': { 
    definition: 'A series of actions or operations taken to achieve an end', 
    category: 'Procedure',
    synonym: 'Procedure'
  },
  'ANALYZE': { 
    definition: 'To examine or study something in order to understand it', 
    category: 'Analysis',
    synonym: 'Examine'
  },
  'DEVELOP': { 
    definition: 'To grow or cause something to grow or become more advanced', 
    category: 'Development',
    synonym: 'Improve'
  },
  'IMPROVE': { 
    definition: 'To make something better or more effective', 
    category: 'Enhancement',
    synonym: 'Enhance'
  },
  'REFLECT': { 
    definition: 'To think carefully and quietly about something', 
    category: 'Thought',
    synonym: 'Ponder'
  },
  'ACHIEVE': { 
    definition: 'To successfully bring about or accomplish something', 
    category: 'Success',
    synonym: 'Attain'
  },
  'CONCEPT': { 
    definition: 'An idea or notion', 
    category: 'Idea',
    synonym: 'Notion'
  },
  'INSIGHT': { 
    definition: 'The ability to understand people and things', 
    category: 'Understanding',
    synonym: 'Perception'
  },
  'COMPLEX': { 
    definition: 'Composed of many different and connected parts', 
    category: 'Complexity',
    synonym: 'Complicated'
  },
  'DYNAMIC': { 
    definition: 'Characterized by constant change or progress', 
    category: 'Change',
    synonym: 'Changing'
  },
  'ENHANCE': { 
    definition: 'To increase or improve something', 
    category: 'Enhancement',
    synonym: 'Improve'
  },
  'EXPLORE': { 
    definition: 'To investigate or examine something in order to gain a deeper understanding', 
    category: 'Investigation',
    synonym: 'Investigate'
  },
  'PERFORM': { 
    definition: 'To carry out or fulfill a particular action or task', 
    category: 'Action',
    synonym: 'Execute'
  },
  
  // Hard words
  'COGNITION': { 
    definition: 'The mental action or process of acquiring knowledge through thought and experience', 
    category: 'Psychology',
    synonym: 'Thinking'
  },
  'CHALLENGE': { 
    definition: 'A task or situation that tests abilities or resources', 
    category: 'Problem-solving',
    synonym: 'Test'
  },
  'INTELLECT': { 
    definition: 'The faculty of reasoning and understanding objectively', 
    category: 'Cognition',
    synonym: 'Mind'
  },
  'KNOWLEDGE': { 
    definition: 'Facts, information, and skills acquired through experience or education', 
    category: 'Learning',
    synonym: 'Understanding'
  },
  'MINDFULNESS': { 
    definition: 'The practice of maintaining awareness of thoughts, feelings, and surroundings', 
    category: 'Psychology',
    synonym: 'Awareness'
  },
  'PSYCHOLOGY': { 
    definition: 'The scientific study of the human mind and behavior', 
    category: 'Science',
    synonym: 'Behavioral science'
  },
  'ALGORITHM': { 
    definition: 'A set of rules or processes for solving a particular problem', 
    category: 'Computer Science',
    synonym: 'Procedure'
  },
  'REASONING': { 
    definition: 'The process of forming conclusions or making decisions based on evidence or reasoning', 
    category: 'Logic',
    synonym: 'Logic'
  },
  'CREATIVITY': { 
    definition: 'The ability to create new and original ideas', 
    category: 'Imagination',
    synonym: 'Imagination'
  },
  'PERCEPTION': { 
    definition: 'The process of becoming aware of something through the senses', 
    category: 'Awareness',
    synonym: 'Awareness'
  },
  'INNOVATION': { 
    definition: 'The act of introducing new or improved methods, products, or processes', 
    category: 'Progress',
    synonym: 'Improvement'
  },
  'ANALYTICAL': { 
    definition: 'Relating to or using analysis or logical reasoning', 
    category: 'Analysis',
    synonym: 'Logical'
  },
  'STRATEGIC': { 
    definition: 'Relating to or forming part of a long-term plan or strategy', 
    category: 'Planning',
    synonym: 'Tactical'
  },
  'DISCIPLINE': { 
    definition: 'A particular area of study or activity', 
    category: 'Study',
    synonym: 'Field'
  },
  'RESILIENCE': { 
    definition: 'The ability to withstand or recover quickly from difficult conditions', 
    category: 'Strength',
    synonym: 'Strength'
  },
  'DEDICATION': { 
    definition: 'The act of giving your time, energy, or resources to something', 
    category: 'Commitment',
    synonym: 'Commitment'
  },
  'COMPREHEND': { 
    definition: 'To understand or grasp the meaning of something', 
    category: 'Understanding',
    synonym: 'Understand'
  },
  'VISUALIZE': { 
    definition: 'To form a mental image or picture of something', 
    category: 'Imagination',
    synonym: 'Imagine'
  }
};

const WordScramble = () => {
  const { gameId } = useParams();
  const { user } = useAuth();
  const [game, setGame] = useState(null);
  const [currentWord, setCurrentWord] = useState('');
  const [scrambledWord, setScrambledWord] = useState('');
  const [userInput, setUserInput] = useState('');
  const [score, setScore] = useState(0);
  const [wordsCompleted, setWordsCompleted] = useState(0);
  const [difficulty, setDifficulty] = useState('easy');
  const [gameStarted, setGameStarted] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState(0);
  const [hintsRemaining, setHintsRemaining] = useState(3);
  const [showHint, setShowHint] = useState(false);
  const [hintType, setHintType] = useState('first-letter');
  const [hintLevel, setHintLevel] = useState(0);
  const [usedWords, setUsedWords] = useState({});

  // Initialize game
  useEffect(() => {
    const fetchGame = async () => {
      try {
        const { data, error } = await gameService.getGameById(gameId);
        
        if (error) {
          console.error('Error fetching game:', error);
          return;
        }
        
        console.log('Loaded word scramble game data:', data); // Debug log
        
        // Set the game data directly, not the wrapper object
        setGame(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching game:', error);
      }
    };
    fetchGame();
  }, [gameId]);

  // Timer effect
  useEffect(() => {
    let interval;
    if (gameStarted) {
      interval = setInterval(() => {
        setCurrentTime(Date.now() - startTime);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameStarted, startTime]);

  const scrambleWord = (word) => {
    let scrambled = word.split('');
    for (let i = scrambled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [scrambled[i], scrambled[j]] = [scrambled[j], scrambled[i]];
    }
    return scrambled.join('');
  };

  const getNewWord = useCallback(() => {
    const words = wordLists[difficulty];
    let word;
    do {
      word = words[Math.floor(Math.random() * words.length)];
    } while (usedWords[word]);
    
    setCurrentWord(word);
    setScrambledWord(scrambleWord(word));
    setUserInput('');
    setShowHint(false);
    setHintLevel(0);
    setUsedWords((prevUsedWords) => ({ ...prevUsedWords, [word]: true }));
  }, [difficulty, usedWords]);

  const initializeGame = (selectedDifficulty) => {
    setDifficulty(selectedDifficulty);
    setScore(0);
    setWordsCompleted(0);
    setErrors(0);
    setHintsRemaining(10);
    setHintLevel(0);
    setStartTime(Date.now());
    setGameStarted(true);
    setUsedWords({});
    getNewWord();
  };

  const handleSubmit = () => {
    if (userInput.toUpperCase() === currentWord) {
      // Calculate word score based on difficulty and time taken
      const wordScore = {
        easy: 100,
        medium: 200,
        hard: 300,
      }[difficulty];

      setScore(score + wordScore);
      setWordsCompleted(wordsCompleted + 1);

      if (wordsCompleted + 1 >= 10) {
        // Game complete
        setGameStarted(false);
        setShowScoreDialog(true);
      } else {
        getNewWord();
      }
    } else {
      setErrors(errors + 1);
    }
  };

  const useHint = () => {
    if (hintsRemaining > 0) {
      setHintsRemaining(hintsRemaining - 1);
      setShowHint(true);
      
      // Cycle through different hint types based on how many hints have been used
      if (hintLevel === 0) {
        setHintType('first-letter');
      } else if (hintLevel === 1) {
        setHintType('category');
      } else if (hintLevel === 2) {
        setHintType('definition');
      } else if (hintLevel === 3) {
        setHintType('synonym');
      } else if (hintLevel === 4) {
        setHintType('length-pattern');
      }
      
      setHintLevel(hintLevel + 1);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
          Word Scramble Challenge
        </Typography>

        {!gameStarted ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Select Difficulty
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {['easy', 'medium', 'hard'].map((level) => (
                <Button
                  key={level}
                  variant="contained"
                  onClick={() => initializeGame(level)}
                  sx={{
                    minWidth: 120,
                    textTransform: 'capitalize',
                  }}
                >
                  {level}
                </Button>
              ))}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                maxWidth: 600,
                mb: 2,
              }}
            >
              <Typography variant="h6">
                Score: {score}
              </Typography>
              <Typography variant="h6">
                Words: {wordsCompleted}/10
              </Typography>
              <Typography variant="h6">
                Time: {Math.floor(currentTime / 1000)}s
              </Typography>
            </Box>

            <Paper
              elevation={3}
              sx={{
                p: 4,
                width: '100%',
                maxWidth: 600,
                background: 'rgba(19, 47, 76, 0.4)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 3,
                }}
              >
                <Typography variant="h5" gutterBottom>
                  Unscramble the word:
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                  }}
                >
                  <AnimatePresence mode="wait">
                    {scrambledWord.split('').map((letter, index) => (
                      <LetterTile
                        key={`${letter}-${index}`}
                        letter={letter}
                        index={index}
                      />
                    ))}
                  </AnimatePresence>
                </Box>

                <Box sx={{ width: '100%', maxWidth: 400 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value.toUpperCase())}
                    placeholder="Type your answer"
                    onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!userInput}
                  >
                    Submit
                  </Button>
                  <IconButton onClick={getNewWord} color="primary">
                    <RefreshIcon />
                  </IconButton>
                  <Tooltip title={hintsRemaining > 0 ? `Hints remaining: ${hintsRemaining}` : 'No hints remaining'}>
                    <span>
                      <IconButton
                        onClick={useHint}
                        color="secondary"
                        disabled={hintsRemaining === 0}
                      >
                        <HintIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                {showHint && (
                  <Typography
                    variant="body2"
                    color="secondary"
                    sx={{ mt: 2 }}
                  >
                    {hintType === 'first-letter' && (
                      <>Hint: The word starts with "{currentWord[0]}"</>
                    )}
                    {hintType === 'category' && wordClues[currentWord] && (
                      <>Hint: Category - {wordClues[currentWord].category}</>
                    )}
                    {hintType === 'definition' && wordClues[currentWord] && (
                      <>Hint: Definition - {wordClues[currentWord].definition}</>
                    )}
                    {hintType === 'synonym' && wordClues[currentWord] && (
                      <>Hint: Synonym - {wordClues[currentWord].synonym}</>
                    )}
                    {hintType === 'length-pattern' && (
                      <>Hint: Pattern - {currentWord.split('').map((letter, idx) => 
                        idx === 0 || idx === currentWord.length - 1 ? letter : '_').join(' ')}</>
                    )}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={`Errors: ${errors}`}
                    color={errors > 5 ? 'error' : 'default'}
                  />
                  <Chip
                    label={`Hints: ${hintsRemaining}`}
                    color="secondary"
                  />
                </Box>
              </Box>
            </Paper>
          </Box>
        )}

        <ScoreSubmission
          open={showScoreDialog}
          onClose={() => setShowScoreDialog(false)}
          gameData={game}
          scoreData={{
            score: score,
            duration: Math.floor(currentTime / 1000),
            difficulty,
            errors,
          }}
          onSubmitSuccess={() => {
            console.log('Word Scramble score submitted successfully');
            setShowScoreDialog(false);
            setGameStarted(false);
          }}
        />
      </Box>
    </Container>
  );
};

export default WordScramble; 